import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon, 
  XMarkIcon,
  PlusIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import toast from 'react-hot-toast';

const StockTransferForm = () => {
  const navigate = useNavigate();
  const { 
    products, 
    fetchAllData, 
    addStockMovement, 
    updateProductStock 
  } = useAppData();
  
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    sourceLocation: 'LOC001',
    destinationLocation: 'LOC002',
    reference: `TR-${Date.now().toString().slice(-6)}`,
    notes: ''
  });
  
  const locations = [
    { id: 'LOC001', name: 'Entrepôt Principal' },
    { id: 'LOC002', name: 'Magasin Centre' },
    { id: 'LOC003', name: 'Magasin Est' }
  ];

  // Ensure we have the latest product data
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);
  
  // Filter products for search
  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.id.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Select a product and add it to the transfer list
  const handleSelectProduct = (product) => {
    // Check if already added
    if (selectedProducts.some(p => p.id === product.id)) {
      toast.error('Ce produit est déjà dans la liste');
      return;
    }
    
    // Add with default quantity of 1
    setSelectedProducts([...selectedProducts, {
      ...product,
      transferQuantity: 1
    }]);
    
    // Reset search
    setSearchTerm('');
    setShowSearch(false);
  };
  
  // Remove a product from the transfer list
  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };
  
  // Update quantity for a product in the transfer
  const handleQuantityChange = (index, value) => {
    const newQuantity = parseInt(value) || 0;
    const product = selectedProducts[index];
    
    // Validate quantity against available stock
    const availableStock = parseInt(product.stock) || 0;
    
    if (newQuantity > availableStock) {
      toast.error(`Stock disponible: ${availableStock}`);
      // Set to max available
      const updatedProducts = [...selectedProducts];
      updatedProducts[index] = { ...product, transferQuantity: availableStock };
      setSelectedProducts(updatedProducts);
      return;
    }
    
    // Update quantity
    const updatedProducts = [...selectedProducts];
    updatedProducts[index] = { ...product, transferQuantity: newQuantity };
    setSelectedProducts(updatedProducts);
  };
  
  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Submit the transfer
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    
    // Validation
    if (selectedProducts.length === 0) {
      toast.error('Veuillez sélectionner au moins un produit');
      return;
    }
    
    if (formData.sourceLocation === formData.destinationLocation) {
      toast.error('L\'emplacement source et la destination doivent être différents');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create stock movement for each product
      for (const product of selectedProducts) {
        // First remove from source
        await updateProductStock(product.id, {
          type: 'sortie',
          quantity: product.transferQuantity
        });
        
        // Create the transfer movement
        const transferMovement = {
          id: `Tra-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
          date: formData.date,
          type: 'transfert',
          productId: product.id,
          product: product.name,
          quantity: product.transferQuantity,
          sourceLocation: formData.sourceLocation,
          destinationLocation: formData.destinationLocation,
          reference: formData.reference,
          notes: formData.notes,
          status: 'in-transit'
        };
        
        addStockMovement(transferMovement);
      }
      
      toast.success('Transfert créé avec succès');
      navigate('/stock');
    } catch (error) {
      console.error('Error creating transfer:', error);
      toast.error('Erreur lors de la création du transfert');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-8">
      <div className="flex items-center">
        <button
          onClick={() => navigate('/stock')}
          className="mr-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h1 className="text-2xl font-bold text-white flex items-center">
          <ArrowsRightLeftIcon className="h-6 w-6 mr-2" /> 
          Nouveau Transfert de Stock
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-white">Date</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className="mt-1 block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label htmlFor="reference" className="block text-sm font-medium text-white">Référence</label>
            <input
              type="text"
              id="reference"
              name="reference"
              value={formData.reference}
              onChange={handleInputChange}
              className="mt-1 block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label htmlFor="sourceLocation" className="block text-sm font-medium text-white">Emplacement Source</label>
            <div className="relative mt-1">
              <select
                id="sourceLocation"
                name="sourceLocation"
                value={formData.sourceLocation}
                onChange={handleInputChange}
                className="block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                required
              >
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-3 top-3 h-5 w-5 text-white/70" />
            </div>
          </div>
          
          <div>
            <label htmlFor="destinationLocation" className="block text-sm font-medium text-white">Destination</label>
            <div className="relative mt-1">
              <select
                id="destinationLocation"
                name="destinationLocation"
                value={formData.destinationLocation}
                onChange={handleInputChange}
                className="block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                required
              >
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-3 top-3 h-5 w-5 text-white/70" />
            </div>
          </div>
        </div>
        
        {/* Product Selection */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white">Produits à transférer</h2>
            <button
              type="button"
              onClick={() => setShowSearch(true)}
              className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Ajouter un produit</span>
            </button>
          </div>
          
          {/* Products Table */}
          {selectedProducts.length > 0 ? (
            <div className="overflow-x-auto mt-4">
              <table className="w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Stock actuel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Quantité à transférer</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {selectedProducts.map((product, index) => (
                    <tr key={product.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
                            {product.name.charAt(0)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{product.name}</div>
                            <div className="text-sm text-white/60">{product.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{product.stock || 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          min="1"
                          max={product.stock || 0}
                          value={product.transferQuantity}
                          onChange={(e) => handleQuantityChange(index, e.target.value)}
                          className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white w-20 text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveProduct(product.id)}
                          className="text-red-400 hover:text-red-500 transition-colors"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-white/60">
              <ArrowsRightLeftIcon className="h-12 w-12 mx-auto text-white/30 mb-4" />
              <p className="text-lg font-medium text-white mb-1">Aucun produit sélectionné</p>
              <p className="text-white/50 mb-4">Ajoutez des produits pour créer un transfert</p>
              <button
                type="button"
                onClick={() => setShowSearch(true)}
                className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors inline-flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Ajouter un produit</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Product Search Modal */}
        {showSearch && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gray-900/95 rounded-3xl p-6 max-w-2xl w-full border border-white/10"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Sélectionner un produit</h2>
                <button
                  type="button"
                  onClick={() => setShowSearch(false)}
                  className="text-white/70 hover:text-white"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="relative mb-6">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par nom ou ID..."
                  className="w-full bg-white/10 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-3.5 h-5 w-5 text-white/50" />
              </div>
              
              <div className="h-96 overflow-y-auto">
                {filteredProducts.length > 0 ? (
                  <ul className="divide-y divide-white/10">
                    {filteredProducts.map(product => (
                      <li key={product.id} className="py-3">
                        <button
                          type="button"
                          onClick={() => handleSelectProduct(product)}
                          className="w-full flex items-center justify-between hover:bg-white/5 p-2 rounded-xl transition-colors"
                        >
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
                              {product.name.charAt(0)}
                            </div>
                            <div className="ml-4 text-left">
                              <div className="text-sm font-medium text-white">{product.name}</div>
                              <div className="text-xs text-white/60">{product.id}</div>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              (parseInt(product.stock) || 0) > 0 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              Stock: {product.stock || 0}
                            </span>
                            <PlusIcon className="ml-2 h-5 w-5 text-blue-400" />
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-12 text-white/60">
                    <p className="text-lg font-medium text-white">Aucun produit trouvé</p>
                    <p className="text-white/50">Essayez un autre terme de recherche</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Notes Field */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <label htmlFor="notes" className="block text-sm font-medium text-white mb-2">Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows="3"
            className="block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Informations complémentaires sur ce transfert..."
          ></textarea>
        </div>
        
        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/stock')}
            className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isLoading || selectedProducts.length === 0}
            className={`px-6 py-3 rounded-xl font-semibold transition-colors flex items-center space-x-2 
              ${isLoading || selectedProducts.length === 0
                ? 'bg-blue-500/50 text-white/70'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
              }`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                <span>Traitement en cours...</span>
              </>
            ) : (
              <span>Créer le transfert</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StockTransferForm;